import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { usePermission } from '../hooks/usePermission';
import * as annApi from '../api/announcements';
import { toast } from '../components/common/Toast';
import Modal from '../components/common/Modal';
import './Pages.css';

// ISS-047 — 백엔드 정책과 정합 (`ANNOUNCEMENT_*` 환경변수 기본값).
const MAX_IMAGES = 10;
const MAX_SIZE_MB = 5;
const ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'webp'];
const ALLOWED_ACCEPT = 'image/png,image/jpeg,image/webp';

const validateClientSide = (file: File): string | null => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXT.includes(ext)) return `허용되지 않은 확장자: ${ext}`;
  if (file.size > MAX_SIZE_MB * 1024 * 1024) return `${MAX_SIZE_MB}MB 초과: ${file.name}`;
  return null;
};

const AnnouncementsPage: React.FC = () => {
  const { canAccess } = usePermission();
  const canManage = canAccess('announcements_manage');

  const [items, setItems] = useState<annApi.Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<annApi.Announcement | null>(null);

  const [form, setForm] = useState({ title: '', content: '', category: 'notice' });
  // ISS-047 — 등록 모달 첨부 파일 (최대 10장).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ISS-027: useCallback 메모화 — 이펙트 의존성에 fetchList 포함 가능. page/canManage 변경 시에만 참조 갱신.
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await annApi.getList({ page, limit: 20, include_inactive: canManage });
      setItems(res.items);
      setTotal(res.total);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [page, canManage]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const resetCreateForm = () => {
    setForm({ title: '', content: '', category: 'notice' });
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePendingFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (pendingFiles.length + picked.length > MAX_IMAGES) {
      toast.error(`이미지는 최대 ${MAX_IMAGES}장까지 첨부할 수 있습니다`);
      return;
    }
    const valid: File[] = [];
    for (const f of picked) {
      const err = validateClientSide(f);
      if (err) { toast.error(err); continue; }
      valid.push(f);
    }
    setPendingFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('제목과 내용을 입력해주세요');
      return;
    }
    let ann: annApi.Announcement;
    try {
      ann = await annApi.create(form);
    } catch (err) {
      if (axios.isAxiosError(err)) toast.error(err.response?.data?.error || '등록 실패');
      return;
    }
    // ISS-047 — 공지 생성 후 이미지를 직렬 업로드. 부분 실패 시 사용자 안내.
    let failures = 0;
    for (const f of pendingFiles) {
      try {
        await annApi.uploadImage(ann.id, f);
      } catch {
        failures += 1;
      }
    }
    if (failures > 0) {
      toast.error(`공지 등록 완료. 단, ${failures}개 이미지 업로드 실패`);
    } else {
      toast.success('공지가 등록되었습니다');
    }
    setCreateOpen(false);
    resetCreateForm();
    fetchList();
  };

  const openEdit = (ann: annApi.Announcement) => {
    setEditTarget(ann);
    setForm({ title: ann.title, content: ann.content, category: ann.category });
    setEditOpen(true);
  };

  const refreshEditTarget = async (annId: number) => {
    try {
      const fresh = await annApi.getById(annId);
      setEditTarget(fresh);
    } catch { /* ignore */ }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    try {
      await annApi.update(editTarget.id, form);
      toast.success('공지가 수정되었습니다');
      setEditOpen(false);
      setEditTarget(null);
      fetchList();
    } catch (err) {
      if (axios.isAxiosError(err)) toast.error(err.response?.data?.error || '수정 실패');
    }
  };

  const handleSoftDelete = async (id: number) => {
    if (!window.confirm('비활성화 하시겠습니까?')) return;
    try {
      await annApi.softDelete(id);
      toast.success('공지가 비활성화되었습니다');
      fetchList();
    } catch (err) {
      if (axios.isAxiosError(err)) toast.error(err.response?.data?.error || '실패');
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      await annApi.reactivate(id);
      toast.success('공지가 재활성화되었습니다');
      fetchList();
    } catch (err) {
      if (axios.isAxiosError(err)) toast.error(err.response?.data?.error || '재활성화 실패');
    }
  };

  const handleHardDelete = async (id: number) => {
    if (!window.confirm('영구 삭제하시겠습니까? 첨부 이미지도 함께 삭제됩니다.')) return;
    try {
      await annApi.hardDelete(id);
      toast.success('공지가 영구 삭제되었습니다');
      fetchList();
    } catch (err) {
      if (axios.isAxiosError(err)) toast.error(err.response?.data?.error || '실패');
    }
  };

  const handleImageUpload = async (annId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_ACCEPT;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const err = validateClientSide(file);
      if (err) { toast.error(err); return; }
      try {
        await annApi.uploadImage(annId, file);
        toast.success('이미지가 업로드되었습니다');
        fetchList();
        if (editTarget && editTarget.id === annId) await refreshEditTarget(annId);
      } catch (e) {
        if (axios.isAxiosError(e)) toast.error(e.response?.data?.error || '업로드 실패');
      }
    };
    input.click();
  };

  const handleEditImageDelete = async (imageId: string) => {
    if (!editTarget) return;
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) return;
    try {
      await annApi.deleteImage(imageId);
      toast.success('이미지가 삭제되었습니다');
      await refreshEditTarget(editTarget.id);
      fetchList();
    } catch (err) {
      if (axios.isAxiosError(err)) toast.error(err.response?.data?.error || '삭제 실패');
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div className="page-header">
        <h2>공지사항 관리</h2>
        {canManage && (
          <button
            className="btn btn--primary"
            onClick={() => {
              // ISS-053 — 등록 모달 열기 직전 form reset (편집 잔존값 차단)
              resetCreateForm();
              setCreateOpen(true);
            }}
          >
            공지 등록
          </button>
        )}
      </div>

      {loading ? (
        <p>로딩 중...</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>카테고리</th>
              <th>제목</th>
              <th>상태</th>
              <th>이미지</th>
              <th>수정일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {items.map((ann) => {
              // ISS-026: 부모 <tr> 에 opacity 를 걸면 자식의 opacity 로 오버라이드 불가하여
              // 재활성화 버튼까지 흐릿해진다. 비활성 시 개별 셀/버튼에 dim 을 적용하고
              // 재활성화 버튼에는 적용하지 않는다.
              const dim = ann.is_active ? undefined : { opacity: 0.5 };
              return (
                <tr key={ann.id}>
                  <td style={dim}>{ann.id}</td>
                  <td style={dim}>{ann.category}</td>
                  <td style={dim}>{ann.title}</td>
                  <td style={dim}>{ann.is_active ? '활성' : '비활성'}</td>
                  <td style={dim}>{ann.images.length}장</td>
                  <td style={{ fontSize: 12, ...(dim || {}) }}>{new Date(ann.updated_at).toLocaleString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn--sm" style={dim} onClick={() => openEdit(ann)}>편집</button>
                      <button className="btn btn--sm" style={dim} onClick={() => handleImageUpload(ann.id)}>📷</button>
                      {canManage && ann.is_active && (
                        <button className="btn btn--sm btn--danger" onClick={() => handleSoftDelete(ann.id)}>비활성</button>
                      )}
                      {canManage && !ann.is_active && (
                        <button className="btn btn--sm btn--primary" onClick={() => handleReactivate(ann.id)}>재활성화</button>
                      )}
                      {canManage && (
                        <button className="btn btn--sm btn--danger" style={dim} onClick={() => handleHardDelete(ann.id)}>삭제</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#999' }}>공지가 없습니다</td></tr>
            )}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
        <span style={{ lineHeight: '32px', fontSize: 13 }}>{page} / {totalPages || 1}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
      </div>

      <Modal
        isOpen={createOpen}
        onClose={() => { setCreateOpen(false); resetCreateForm(); }}
        title="공지 등록"
        onConfirm={handleCreate}
        confirmText="등록"
      >
        <div className="form-group">
          <label>카테고리</label>
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </div>
        <div className="form-group">
          <label>제목</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} />
        </div>
        <div className="form-group">
          <label>내용</label>
          <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} style={{ width: '100%' }} />
        </div>
        <div className="form-group">
          <label>이미지 첨부 (선택, 최대 {MAX_IMAGES}장 / 1장당 {MAX_SIZE_MB}MB)</label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_ACCEPT}
            onChange={handlePendingFilesChange}
          />
          {pendingFiles.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {pendingFiles.map((f, idx) => (
                <div key={`${f.name}-${idx}`} style={{ position: 'relative', border: '1px solid #ddd', borderRadius: 4, padding: 4 }}>
                  <img
                    src={URL.createObjectURL(f)}
                    alt={f.name}
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 2 }}
                  />
                  <button
                    type="button"
                    onClick={() => removePendingFile(idx)}
                    style={{
                      position: 'absolute', top: 0, right: 0,
                      background: '#F44336', color: '#fff', border: 'none',
                      borderRadius: '50%', width: 20, height: 20, cursor: 'pointer',
                      fontSize: 12, lineHeight: '20px',
                    }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal isOpen={editOpen} onClose={() => { setEditOpen(false); resetCreateForm(); }} title="공지 편집" onConfirm={handleEdit} confirmText="저장">
        <div className="form-group">
          <label>카테고리</label>
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </div>
        <div className="form-group">
          <label>제목</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} />
        </div>
        <div className="form-group">
          <label>내용</label>
          <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} style={{ width: '100%' }} />
        </div>
        {editTarget && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 600 }}>첨부 이미지 ({editTarget.images.length}/{MAX_IMAGES})</label>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => handleImageUpload(editTarget.id)}
                disabled={editTarget.images.length >= MAX_IMAGES}
              >
                이미지 추가
              </button>
            </div>
            {editTarget.images.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                {editTarget.images.map((img) => (
                  <div key={img.id} style={{ position: 'relative' }}>
                    <img src={img.image_url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                    <button
                      type="button"
                      onClick={() => handleEditImageDelete(img.id)}
                      title="이미지 삭제"
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        background: '#F44336', color: '#fff', border: 'none',
                        borderRadius: '50%', width: 20, height: 20, cursor: 'pointer',
                        fontSize: 12, lineHeight: '20px',
                      }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AnnouncementsPage;
